import { colorHex } from '@ramp/theme';
import { MEDIA_BASE_URL } from 'api/_baseUrl';
import { getAccessToken } from 'axios-jwt';
import { decodeToken } from 'react-jwt';
import { useLayoutStore } from 'store/layout';
import { User } from 'types';
import { FirmTheme } from 'types/api/firms';
import { themeSource } from 'utils/themeSource';

// Checked by hand, not with yup: this runs at store init, and yup would add itself and lodash to the initial bundle.
const COLOR_SCHEME_SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];

const isFilledString = (value: unknown) => typeof value === 'string' && value !== '';

const isColorScheme = (value: any) => !!value && COLOR_SCHEME_SHADES.every(shade => isFilledString(value[shade]));

const hasThemeLogos = (value: any) =>
  !!value &&
  ['logo_primary_light', 'logo_primary_dark', 'logo_secondary_light', 'logo_secondary_dark', 'theme'].every(key =>
    isFilledString(value[key])
  );

const isOldTheme = (value: any) => hasThemeLogos(value) && isColorScheme(value.color_scheme);

const isTheme = (value: any) =>
  hasThemeLogos(value) && isColorScheme(value.color_scheme_light) && isColorScheme(value.color_scheme_dark);

export const getDefaultTheme: () => FirmTheme = () => ({
  logo_primary_light: `${MEDIA_BASE_URL}/logo_primary_light.svg`,
  logo_primary_dark: `${MEDIA_BASE_URL}/logo_primary_dark.svg`,
  logo_secondary_light: `${MEDIA_BASE_URL}/logo_secondary_light.svg`,
  logo_secondary_dark: `${MEDIA_BASE_URL}/logo_secondary_dark.svg`,
  theme: 'dark',
  color_scheme_light: {
    50: '#FFFAFB',
    100: '#FEDDE2',
    200: '#FB9DAB',
    300: '#F86279',
    400: '#F62847',
    500: '#D60928',
    600: '#AB0720',
    700: '#7F0518',
    800: '#53040F',
    900: '#2C0208'
  },
  color_scheme_dark: {
    50: '#FFFAFB',
    100: '#FEDDE2',
    200: '#FB9DAB',
    300: '#F86279',
    400: '#F62847',
    500: '#D60928',
    600: '#AB0720',
    700: '#7F0518',
    800: '#53040F',
    900: '#2C0208'
  }
});

export const getInitialTheme = () => {
  const storedTheme = localStorage.getItem('sc_theme');

  if (!storedTheme) return getDefaultTheme();

  let parsedTheme: any = null;
  try {
    parsedTheme = JSON.parse(storedTheme);
  } catch (e) {}

  // Fallback for handling old theme
  if (isOldTheme(parsedTheme)) {
    const theme: FirmTheme = {
      logo_primary_light: parsedTheme.logo_primary_light,
      logo_primary_dark: parsedTheme.logo_primary_dark,
      logo_secondary_light: parsedTheme.logo_secondary_light,
      logo_secondary_dark: parsedTheme.logo_secondary_dark,
      theme: parsedTheme.theme,
      color_scheme_light: parsedTheme.color_scheme,
      color_scheme_dark: parsedTheme.color_scheme
    };

    localStorage.setItem('sc_theme', JSON.stringify(theme));

    return theme;
  }

  if (isTheme(parsedTheme)) return parsedTheme as FirmTheme;

  const token = getAccessToken()!;

  // Fallback to theme from token
  if (token) {
    const decodedToken: User = decodeToken(token)!;

    const theme = {
      logo_primary_light: decodedToken.logo_primary_light,
      logo_primary_dark: decodedToken.logo_primary_dark,
      logo_secondary_light: decodedToken.logo_secondary_light,
      logo_secondary_dark: decodedToken.logo_secondary_dark,
      theme: decodedToken.theme,
      color_scheme_light:
        decodedToken?.color_scheme_light || (decodedToken as any)?.color_scheme || getDefaultTheme().color_scheme_light,
      color_scheme_dark:
        decodedToken?.color_scheme_dark || (decodedToken as any)?.color_scheme || getDefaultTheme().color_scheme_dark
    };

    localStorage.setItem('sc_theme', JSON.stringify(theme));

    return theme;
  }

  return getDefaultTheme();
};

export const fetchPartnerTheme = async () => {
  const source = themeSource(window.location.search, localStorage.getItem('sc_theme_partner'));

  if (!source) return;

  // Loaded here, not at import: the module runs at store init, and the api barrel is large.
  const { default: api } = await import('api');

  if ('mdm' in source) {
    // Not persisted: the host stays in the enrollment URL, and a stored theme would brand later visits.
    await api.mdm.getEnrollmentBranding(source.mdm).then(theme => {
      useLayoutStore.getState().setTheme(theme);
    });

    return;
  }

  localStorage.setItem('sc_theme_partner', source.partner);

  await api.firms.getFirmsTheme(source.partner).then(theme => {
    useLayoutStore.getState().setTheme(theme, true);
  });
};

export const getChakraColor = (name: string) => {
  return getComputedStyle(document.documentElement).getPropertyValue(
    colorHex(name).replace('var(', '').replace(')', '')
  );
};
